/**
 * Which Mac host should serve the next lease.
 *
 * Pure, and separate from the hook, because "no host was chosen" is the
 * difference between the panel saying `queued` and it saying `allocating` —
 * a user-visible claim about whether a simulator is coming. That should be
 * assertable without a network call.
 *
 * CMMD refuses an over-capacity claim anyway, so this is not the safety check.
 * It exists so the client does not spend a request on a host it can already see
 * is full, and so the choice is deterministic rather than "whatever the list
 * happened to return first".
 */

import type { CmmdSimHost } from "../../environments/cmmdApi";
import type { LeaseState } from "./leaseToPanelState";

/** Free slots on a host; never negative, so a mis-counted host reads as full. */
function freeSlots(host: CmmdSimHost): number {
  return Math.max(0, host.maxConcurrentLeases - host.liveLeases);
}

/**
 * The emptiest host with room, or null when every host is full.
 *
 * Emptiest rather than first: picking the first available piles every lease
 * onto one machine while the others idle, and a single host's simulators
 * contend for the same CPU. Ties break on the lowest id so the choice is
 * reproducible — a shuffling selection makes a failed claim impossible to
 * retrace.
 */
export function chooseSimHost(hosts: ReadonlyArray<CmmdSimHost>): CmmdSimHost | null {
  let best: CmmdSimHost | null = null;
  let bestFree = 0;

  for (const host of hosts) {
    const free = freeSlots(host);
    if (free === 0) continue;
    if (best === null || free > bestFree || (free === bestFree && host.id < best.id)) {
      best = host;
      bestFree = free;
    }
  }

  return best;
}

/**
 * How many leases are queued ahead of this one.
 *
 * Only meaningful when nothing is claimable: with a host free, the answer is 0
 * however busy the fleet is, because this lease is about to be served. Reporting
 * a positive number then would put the panel in `queued` while a simulator was
 * already being allocated.
 */
export function queueDepth(hosts: ReadonlyArray<CmmdSimHost>): number {
  if (chooseSimHost(hosts) !== null) return 0;
  return hosts.reduce((total, host) => total + host.liveLeases, 0);
}

/**
 * Whether a claim should be attempted right now.
 *
 * Consent is not a claim. A user can agree to use a simulator while every host
 * is full, and the lease has to be taken later — when one frees up — rather than
 * never. Without this, consenting to a busy fleet leaves the panel in `queued`
 * permanently, since polling only reads state and never acts on it.
 *
 * Pure so the "claim again" and "do not claim again" cases are assertable
 * without a timer: re-claiming a lease already held would fail the identity
 * check anyway, but re-claiming a BLOCKED one would retry a terminal state
 * forever.
 */
export function shouldClaimNow(input: {
  readonly consentGiven: boolean;
  readonly leaseState: LeaseState | null;
  readonly hasFreeHost: boolean;
}): boolean {
  if (!input.consentGiven || !input.hasFreeHost) return false;

  switch (input.leaseState) {
    // Nothing yet, or a finished lease whose row can be recycled.
    case null:
    case "released":
      return true;
    // `allocating` / `active` are already ours; `releasing` is mid-handback and
    // claiming across it would race the release; `blocked` is terminal.
    default:
      return false;
  }
}
